// Обзор кандидата: кто он, что выяснено про квалификацию, что показала сверка по чёрному
// списку, метки и заметки рекрутёра.
//
// Порядок блоков — это порядок вопросов, которые задаёт рекрутёр, а не порядок полей в базе.
// Сверка стоит сразу за именем: она меняет решение по человеку целиком, и узнавать её после
// того, как прочитал квалификацию, поздно.
//
// Правило 7 держится здесь в трёх местах сразу. «Сверки не было» ≠ «чисто». «Про вахту не
// спрашивали» ≠ «на вахту не готов». «Заметок нет» ≠ «заметки не приехали».

import { RiAddLine, RiPriceTag3Line, RiShieldKeyholeLine } from '@remixicon/react';
import { Link, useNavigate } from 'react-router';

import { formatRubles } from '@/shared/money/format';
import { formatPhone } from '@/shared/text/phone';
import { formatDateOnly, parseMoment } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import * as LinkButton from '@/shared/ui/link-button';

import type { CandidateOut } from '../api/candidates';
import { BlacklistResultCard } from '../components/BlacklistResult';
import { GateNote, useCan } from '../components/gates';
import { DuplicateNotice } from '../components/DuplicateNotice';
import { useCheckCandidateMutation, useDuplicateWarning } from '../queries';
import type { HiringRoute } from '../route';
import {
  FILTER_CANDIDATE,
  PANEL_CANDIDATE_NOTE,
  PANEL_CANDIDATE_TAGS,
  PANEL_NEW_ENTRY,
  hiringHref,
  openPanelHref,
} from '../route';

const EDIT_PERMISSION = 'hiring.candidate.edit';
const CHECK_PERMISSION = 'hiring.blacklist.check';
const BLACKLIST_EDIT_PERMISSION = 'hiring.blacklist.edit';
/** Журнал сверок открыт по тому же праву, что и сам чёрный список. */
const BLACKLIST_VIEW_PERMISSION = 'hiring.blacklist.view';

export function OverviewTab({ candidate, route }: { candidate: CandidateOut; route: HiringRoute }) {
  const navigate = useNavigate();
  const canEdit = useCan(EDIT_PERMISSION);
  const canCheck = useCan(CHECK_PERMISSION);
  const canBlacklist = useCan(BLACKLIST_EDIT_PERMISSION);
  const canViewChecks = useCan(BLACKLIST_VIEW_PERMISSION);
  const сверка = useCheckCandidateMutation(candidate.id);
  const дубли = useDuplicateWarning(candidate.id);

  const квалификация = candidate.qualification ?? null;
  const метки = candidate.tags ?? [];
  const заметки = candidate.notes ?? [];

  return (
    <div className='flex flex-col gap-3 p-5'>
      {/* Предупреждение о дубле приезжает один раз, вместе с только что созданной карточкой,
          и в самой карточке его уже нет: сервер отдаёт его при создании. Показываем его из
          кэша — человек, только что заведший кандидата, обязан увидеть совпадение здесь, а
          не только в мастере, который он уже закрыл. */}
      {дубли !== undefined && (
        <DuplicateNotice
          report={дубли}
          note='Карточка уже создана. Если это тот же человек — склейте карточки: отклики, заметки и метки переедут на оставленную.'
        />
      )}

      <BlacklistResultCard
        result={candidate.blacklist_result}
        checkedAt={candidate.blacklist_checked_at}
        message={сверка.data?.message}
        nextAction={сверка.data?.next_action}
        reasonTitle={сверка.data?.reason_title}
      />

      <div className='flex flex-wrap items-center gap-2'>
        <Button.Root
          type='button'
          variant='neutral'
          mode='stroke'
          size='xsmall'
          onClick={() => {
            if (!canCheck) {
              return; // причина написана ниже, под кнопкой: молчаливого отказа не бывает
            }
            сверка.mutate('manual');
          }}
        >
          <Button.Icon as={RiShieldKeyholeLine} />
          {сверка.isPending ? 'Сверяем…' : 'Сверить по чёрному списку'}
        </Button.Root>
        {canBlacklist && (
          <Button.Root
            type='button'
            variant='neutral'
            mode='ghost'
            size='xsmall'
            onClick={() => navigate(openPanelHref(route, PANEL_NEW_ENTRY, candidate.id))}
          >
            Внести в чёрный список
          </Button.Root>
        )}
        {/* Одна метка отвечает «нашли или нет», а на вопрос «когда сверяли и по какому
            признаку» отвечает журнал. Ссылка ведёт в него уже с отбором по этому человеку:
            искать его там руками среди сотен строк — это отдельная работа ни за что. */}
        {canViewChecks && (
          <LinkButton.Root variant='gray' size='medium' underline asChild>
            <Link
              to={hiringHref('checks', {
                filters: { [FILTER_CANDIDATE]: candidate.id },
                panels: [],
              })}
            >
              Все сверки по этому человеку
            </Link>
          </LinkButton.Root>
        )}
      </div>
      {!canCheck && (
        <GateNote>
          Сверку по списку выполняют рекрутёр, кадры и финансовый отдел — у вашей учётной записи
          этого права нет. Результат прошлой сверки виден выше и без него.
        </GateNote>
      )}
      {сверка.isError && <ErrorAlert error={сверка.error} />}

      <FieldList title='Кто он'>
        <FieldRow label='Фамилия, имя, отчество' value={candidate.display_name} />
        <FieldRow
          label='Телефон'
          value={formatPhone(candidate.phone_normalized)}
          missing='телефон не оставил'
          note={
            candidate.phone_raw !== null && candidate.phone_raw !== candidate.phone_normalized
              ? `как пришло с площадки: ${candidate.phone_raw}`
              : undefined
          }
        />
        <FieldRow
          label='Дата рождения'
          value={candidate.birth_date === null ? null : formatDateOnly(candidate.birth_date)}
          missing='не спрашивали'
        />
        <FieldRow label='Город' value={candidate.city} missing='город не назван' />
        <FieldRow label='Регион' value={candidate.region} missing='регион не назван' />
        <FieldRow
          label='Удостоверение охранника'
          value={candidate.guard_license_no}
          missing='номер не назван'
        />
        {(candidate.extra_contacts ?? []).map((contact, index) => (
          <FieldRow
            key={`${contact.kind}-${index}`}
            label={`Ещё связь: ${contact.kind}`}
            value={contact.value}
          />
        ))}
        <FieldRow
          label='Первый отклик'
          value={
            candidate.first_seen_at === null
              ? null
              : (parseMoment(candidate.first_seen_at)?.toLocaleString('ru-RU') ?? null)
          }
          missing='карточка заведена руками, отклика не было'
        />
      </FieldList>

      <FieldList title='Квалификация'>
        {квалификация === null ? (
          <FieldRow
            label='Про работу ещё не спрашивали'
            value={null}
            missing='Помощник заполняет эти поля по ходу переписки, рекрутёр — руками в карточке. Пустая квалификация означает, что разговора ещё не было, а не что человек не подходит.'
          />
        ) : (
          <>
            <FieldRow
              label='Готов на вахту'
              value={ответДа(квалификация.vahta_ready)}
              missing='не спрашивали'
            />
            <FieldRow
              label='Длительность вахты'
              value={
                квалификация.vahta_length_days == null
                  ? null
                  : `${квалификация.vahta_length_days} дней`
              }
              missing='не спрашивали'
            />
            <FieldRow
              label='Удостоверение охранника есть'
              value={ответДа(квалификация.license_present)}
              missing='не спрашивали'
            />
            <FieldRow
              label='Разряд'
              value={
                квалификация.license_grade == null ? null : `${квалификация.license_grade} разряд`
              }
              missing='не спрашивали'
            />
            <FieldRow
              label='Куда готов ехать'
              value={квалификация.region_ready}
              missing='не спрашивали'
            />
            <FieldRow
              label='Какой объект хочет'
              value={квалификация.site_wanted}
              missing='не спрашивали'
            />
            <FieldRow
              label='Какой график хочет'
              value={квалификация.schedule_wanted}
              missing='не спрашивали'
            />
            <FieldRow
              label='Ожидания по оплате'
              // Число стоит В ФРАЗЕ рядом с полями других единиц (дни, разряд), поэтому знак
              // рубля обязателен: без него «60 000» читается как что угодно.
              value={
                квалификация.pay_expectation_kopecks == null
                  ? null
                  : `${formatRubles(квалификация.pay_expectation_kopecks)}`
              }
              missing='не спрашивали'
            />
          </>
        )}
      </FieldList>

      <FieldList title='Метки'>
        <FieldRow
          label='Метки на карточке'
          value={
            метки.length === 0 ? null : (
              <span className='flex flex-wrap gap-1.5'>
                {метки.map((метка) => (
                  <Badge.Root key={метка} variant='lighter' color='blue' size='medium'>
                    {метка}
                  </Badge.Root>
                ))}
              </span>
            )
          }
          missing='меток нет'
          note={
            canEdit ? (
              <Button.Root
                type='button'
                variant='neutral'
                mode='ghost'
                size='xxsmall'
                onClick={() => navigate(openPanelHref(route, PANEL_CANDIDATE_TAGS, candidate.id))}
              >
                <Button.Icon as={RiPriceTag3Line} />
                Добавить метки
              </Button.Root>
            ) : undefined
          }
        />
      </FieldList>

      <FieldList title='Заметки рекрутёра'>
        {заметки.length === 0 ? (
          <FieldRow
            label='Заметок нет'
            value={null}
            missing='Здесь остаётся то, что не влезает в поля: договорённости по телефону, обещания, странности. Через месяц это единственное, чем восстанавливается разговор.'
          />
        ) : (
          заметки.map((заметка) => (
            <FieldRow
              key={заметка.id}
              label={parseMoment(заметка.created_at)?.toLocaleString('ru-RU') ?? 'без даты'}
              value={заметка.body}
            />
          ))
        )}
        {canEdit && (
          <div className='py-2'>
            <Button.Root
              type='button'
              variant='neutral'
              mode='stroke'
              size='xsmall'
              onClick={() => navigate(openPanelHref(route, PANEL_CANDIDATE_NOTE, candidate.id))}
            >
              <Button.Icon as={RiAddLine} />
              Добавить заметку
            </Button.Root>
          </div>
        )}
      </FieldList>
    </div>
  );
}

/**
 * Ответ «да/нет» из необязательного признака. `null` возвращается наружу, чтобы поле показало
 * «не спрашивали»: «нет» и «не спрашивали» — разные факты (правило 7), и по первому человека
 * отсеивают, а по второму звонят и уточняют.
 */
function ответДа(значение: boolean | null | undefined): string | null {
  if (значение === null || значение === undefined) {
    return null;
  }
  return значение ? 'да' : 'нет';
}
